// The moment between choosing a venue (or an organisation) and arriving at it (spec/13 §2.4): a short full-screen
// overlay with the place's name and the orbit, shown until the new place's data has loaded — at least 350ms, so it
// never flashes, at most 10s, so a slow api never traps anyone behind it — and then faded out. Both consoles use it:
// the venue console's switcher and venue cards, and HQ's organisation links.
//
// "Loaded" is: the router is on a different page than the one the switch began on, and no query is in flight. The
// switcher reads what the member may open at the place before it navigates, so that is part of the wait too.

import { VenueSwitchOverlay } from '@clavian/ui-web';
import { useIsFetching } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/** The longest the overlay waits for data, in milliseconds. */
export const SWITCH_MAX_MS = 10_000;
/** How long "nothing in flight" must hold before the new place counts as loaded, in milliseconds. */
const SETTLE_MS = 150;

interface PlaceSwitch {
  /** Start the transition for a place; the caller then navigates. */
  readonly begin: (name: string) => void;
}

const PlaceSwitchContext = createContext<PlaceSwitch>({ begin: () => undefined });

/** `begin(name)` before navigating to another place. Outside the provider it does nothing. */
export function usePlaceSwitch(): PlaceSwitch {
  return useContext(PlaceSwitchContext);
}

interface Switching {
  readonly name: string;
  readonly from: string;
}

export function PlaceSwitchProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const fetching = useIsFetching();
  const [switching, setSwitching] = useState<Switching | undefined>(undefined);
  const [timedOut, setTimedOut] = useState(false);
  const [settled, setSettled] = useState(false);

  const begin = useCallback(
    (name: string) => {
      setTimedOut(false);
      setSettled(false);
      setSwitching({ name, from: pathname });
    },
    [pathname],
  );
  const api = useMemo<PlaceSwitch>(() => ({ begin }), [begin]);

  const active = switching !== undefined;
  useEffect(() => {
    if (!active) return undefined;
    const timer = setTimeout(() => setTimedOut(true), SWITCH_MAX_MS);
    return () => clearTimeout(timer);
  }, [active]);

  // The page has changed and nothing is in flight — and has stayed so for a beat, because the new page's own queries
  // start a moment after the route commits, and "nothing in flight" at that instant means "not started", not "loaded".
  const quiet = switching !== undefined && pathname !== switching.from && fetching === 0;
  useEffect(() => {
    if (!quiet) return undefined;
    const timer = setTimeout(() => setSettled(true), SETTLE_MS);
    return () => clearTimeout(timer);
  }, [quiet]);

  return (
    <PlaceSwitchContext.Provider value={api}>
      {children}
      {switching === undefined ? null : (
        <VenueSwitchOverlay name={switching.name} ready={settled || timedOut} onFinished={() => setSwitching(undefined)} />
      )}
    </PlaceSwitchContext.Provider>
  );
}
